import {
  FormField,
  Icon,
  Multiselect,
  MultiselectProps,
  SelectProps,
  TextContent,
} from '@amzn/awsui-components-core';
import { OptionDefinition } from '@amzn/awsui-components-core/internal/components/option/interfaces';
import { NonCancelableCustomEvent } from '@amzn/awsui-components-core/internal/events';
import React, { useCallback, useEffect, useState } from 'react';
import constants from '../../../common/constants';
import InfoLink from '../../common/InfoLink';

interface Props {
  onSelectsUpdated?: (
    sectionId: string,
    selectedValues: string[],
    validationError?: string,
  ) => void;
  onValidationError?: (validationError?: string) => void;
  sectionId?: string;
  label: string;
  description?: string;
  contextualHelp?: string;
  readonly?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  showValidationError?: boolean;
  selectedOptions: OptionDefinition[];
  selectOptions: SelectProps.Options;
  stretch?: boolean;
  placeholder?: string;
  filtering?: boolean;
  maxNumber?: number;
}

const MetadataMultiselectInputV3: React.FunctionComponent<Props> = (props: Props) => {
  const {
    readonly = false,
    label,
    description = '',
    contextualHelp,
    stretch = false,
    disabled = false,
    selectOptions,
    placeholder = '',
    isRequired = false,
    filtering = true,
    showValidationError = true,
    maxNumber = 0,
    onValidationError,
    sectionId,
    onSelectsUpdated,
  } = props;

  const [selectedOptions, setSelectedOptions] = useState<readonly OptionDefinition[]>([]);
  const [validationError, setValidationError] = useState<string>();

  const validateInput = useCallback(
    (inputOptions: readonly OptionDefinition[]) => {
      let error: string = '';
      if (maxNumber && maxNumber > 0 && inputOptions.length > maxNumber) {
        error = constants.MAX_SERVICES_EXCEEDED;
      }
      if (isRequired && inputOptions.length === 0) {
        error = constants.REQUIRED;
      }
      if (onValidationError) {
        onValidationError(error);
      }
      return error;
    },
    [maxNumber, isRequired, onValidationError],
  );

  useEffect(() => {
    const filteredOptions = (props.selectedOptions || []).filter((i) => i != null);
    setSelectedOptions(filteredOptions);
    const error = validateInput(filteredOptions);
    setValidationError(error);
  }, [props.selectedOptions, validateInput]);

  const handleInput = (e: NonCancelableCustomEvent<MultiselectProps.MultiselectChangeDetail>) => {
    const updatedOptions = e.detail.selectedOptions;
    const error = validateInput(updatedOptions);
    setSelectedOptions(updatedOptions);
    setValidationError(error);
    updateSectionValues(updatedOptions, error);
  };

  const updateSectionValues = (updatedOptions: readonly OptionDefinition[], error?: string) => {
    if (sectionId && onSelectsUpdated) {
      const values = updatedOptions.map((selectedOption) => selectedOption.value || '');
      onSelectsUpdated(sectionId, values, error);
    }
  };

  if (readonly) {
    const displayMetatada = (
      <details open>
        <summary>{label}</summary>
        <TextContent>
          <ul>
            {selectedOptions.map((selectedOption, idx) => (
              <li key={selectedOption.label}>
                {selectedOption.label} <Icon name={selectedOption.iconName} />
              </li>
            ))}
          </ul>
        </TextContent>
      </details>
    );
    const emptyMetadata = (
      <details open>
        <summary>{label}</summary>
        <ul>No selection</ul>
      </details>
    );
    return selectedOptions.length ? displayMetatada : emptyMetadata;
  } else {
    return (
      <FormField
        errorText={showValidationError && validationError}
        label={
          <span>
            {label}
            {!isRequired && <i> - optional</i>}
          </span>
        }
        info={contextualHelp && <InfoLink topic={contextualHelp} />}
        description={description}
        stretch={stretch}
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <Multiselect
          selectedOptions={selectedOptions}
          onChange={handleInput}
          options={selectOptions}
          placeholder={placeholder}
          disabled={disabled}
          filteringType={filtering ? 'auto' : 'none'}
          keepOpen={false}
          deselectAriaLabel={() => 'Remove option'}
          ariaRequired={isRequired}
        />
      </FormField>
    );
  }
};

export default MetadataMultiselectInputV3;
